import { useEffect, useState } from "react";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";
import { projectDetailsApi } from "../api/projectDetails";
import { AssetProps } from "../components/Asset";
import { useAssetDetails } from "./useAssetDetails";

export const useUpdateAsset = ({ initialAsset, projectId, onSuccess, isEdit }: { initialAsset?: AssetProps, projectId: string, onSuccess: () => void; isEdit: boolean }) => {

    const { removeCache } = useAssetDetails(projectId);

    const [asset, setAsset] = useState<{
        programme: AutocompleteOption | null;
        narms_value: string;
        asset_category: string | null;
        iln_number_id: string | null;
        functional_location: string;
        asset_budget: string;
        asset_description: string;
        asset_type: string | null;
    }>({
        programme: null,
        narms_value: '',
        asset_category: null,
        iln_number_id: null,
        functional_location: '',
        asset_budget: '',
        asset_description: '',
        asset_type: null
    });

    const [assetErrors, setAssetErrors] = useState<{ [key: string]: string }>({});
    const [submitting, setSubmitting] = useState(false);

    const handleAssetChange = (key: string, value: any) => {
        setAsset((prevAsset: any) => ({
            ...prevAsset,
            [key]: value
        }));
    };

    const validateAsset = () => {
        const errors: { [key: string]: string } = {};

        if (!asset.programme) {
            errors.category = "Category is required.";
        }
        if (!asset.narms_value && asset.programme && asset.programme.id == 6) {
            errors.narms_value = "NARMS Value is required.";
        }
        if (!asset.asset_category) {
            errors.asset_category = "Asset Category is required.";
        }
        if (!asset.asset_type) {
            errors.asset_type = "Asset Type is required.";
        }
        if (!asset.iln_number_id) {
            errors.iln_number_id = "ILN Number ID is required.";
        }
        if (!asset.functional_location) {
            errors.functional_location = "Functional Location is required.";
        }
        if (!asset.asset_budget) {
            errors.asset_budget = "Asset Budget is required.";
        }
        if (!asset.asset_description) {
            errors.asset_description = "Asset Description is required.";
        }
        setAssetErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {

        setAssetErrors({});
        setSubmitting(true);

        if (!validateAsset()) {
            setSubmitting(false);
            return;
        }
        try {
            const payload = {
                "asset_details": [
                    {
                        "programme_id": asset?.programme?.id,
                        "asset_category": asset.asset_category,
                        "asset_type": asset.asset_type,
                        "iln_id": asset.iln_number_id,
                        "functional_location": asset.functional_location,
                        "narms_value": Number(asset.narms_value),
                        "asset_budget": Number(asset.asset_budget),
                        "description": asset.asset_description,
                    }
                ]
            };
            if (isEdit && initialAsset) {
                const response = await projectDetailsApi.updateAsset({ projectId, assetId: initialAsset.asset_id, assetData: payload });
                if (response.status) {
                    onSuccess();
                }
            } else {
                const response = await projectDetailsApi.createAsset({ projectId, assetData: payload });
                if (response.status) {
                    onSuccess();
                }
            }
        } catch (error) {
            console.error("Error in asset submission:", error);
        } finally {
            setSubmitting(false);
            setAsset({
                programme: null,
                narms_value: '',
                asset_type: null,
                iln_number_id: null,
                functional_location: '',
                asset_budget: '',
                asset_description: '',
                asset_category: null
            });
            removeCache();
        }

    }

    useEffect(() => {
        if (initialAsset && isEdit) {
            setAsset({
                programme: (initialAsset.programme_id && initialAsset.programme_description) ? { id: initialAsset.programme_id, label: initialAsset.programme_description } : null,
                asset_category: initialAsset.asset_category,
                narms_value: initialAsset.narms_value,
                iln_number_id: initialAsset.iln_id,
                functional_location: initialAsset.functional_location,
                asset_budget: initialAsset.asset_budget,
                asset_description: initialAsset.asset_description,
                asset_type: initialAsset.asset_type,
            });
        }
    }, [initialAsset, isEdit]);

    return {
        asset,
        assetErrors,
        handleAssetChange,
        handleSubmit,
        submitting
    }

};